import React, { useState, useMemo, useEffect } from 'react';
import { ADMIN_NAV_AREAS } from '../constants/adminNav';
import { ADMIN_PROCESS_LINKS } from '../constants/adminProcessLinks';
import './AdminLayout.css';

const SIDEBAR_COLLAPSED_KEY = 'javiland_admin_sidebar_collapsed';

function NavBadge({ count }) {
  const n = Number(count) || 0;
  if (n < 1) return null;
  return (
    <span className="admin-sidebar-badge" aria-label={`${n} pendientes`}>
      {n > 99 ? '99+' : n}
    </span>
  );
}

const AdminLayout = ({
  currentUser,
  activeSection,
  activeQueue = '',
  operationsSummary,
  processKey,
  onNavigate,
  onGoHome,
  onLogout,
  children
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [collapsedAreas, setCollapsedAreas] = useState(() => {
    const init = {};
    ADMIN_NAV_AREAS.forEach((area) => {
      if (area.collapsedDefault) init[area.id] = true;
    });
    return init;
  });

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [sidebarCollapsed]);

  const badges = operationsSummary?.badges || {};

  const activeNavId = useMemo(() => {
    for (const area of ADMIN_NAV_AREAS) {
      for (const item of area.items) {
        const section = item.section || item.id;
        const queue = item.queue ?? '';
        if (section === activeSection && queue === (activeQueue || '')) {
          return item.id;
        }
      }
    }
    if (activeSection === 'dashboard') return 'dashboard';
    return activeSection;
  }, [activeSection, activeQueue]);

  const toggleArea = (areaId) => {
    setCollapsedAreas((prev) => ({ ...prev, [areaId]: !prev[areaId] }));
  };

  const processLink = processKey ? ADMIN_PROCESS_LINKS[processKey] : null;

  return (
    <div className={`admin-shell${sidebarCollapsed ? ' admin-shell--sidebar-collapsed' : ''}`}>
      <aside
        className={`admin-sidebar${sidebarCollapsed ? ' admin-sidebar--collapsed' : ''}`}
        aria-label="Navegación administrativa"
      >
        <div className="admin-sidebar-brand">
          <button
            type="button"
            className="admin-sidebar-toggle"
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={sidebarCollapsed ? 'Expandir menú' : 'Colapsar menú a iconos'}
            title={sidebarCollapsed ? 'Expandir menú' : 'Solo iconos'}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'angles-right' : 'angles-left'}`} aria-hidden />
          </button>
          <img src="/images/brand/logo-corto.jpg" alt="" className="admin-sidebar-logo" />
          {!sidebarCollapsed ? (
            <div className="admin-sidebar-brand-text">
              <strong>Javiland</strong>
              <span>Panel operativo</span>
            </div>
          ) : null}
        </div>

        <nav className="admin-sidebar-nav">
          {ADMIN_NAV_AREAS.map((area) => {
            const isCollapsed = collapsedAreas[area.id];
            return (
              <div key={area.id} className="admin-sidebar-group">
                {!sidebarCollapsed && area.id !== 'today' ? (
                  <button
                    type="button"
                    className="admin-sidebar-group-title"
                    onClick={() => toggleArea(area.id)}
                    aria-expanded={!isCollapsed}
                  >
                    <span>{area.label}</span>
                    <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} aria-hidden />
                  </button>
                ) : null}
                {(sidebarCollapsed || !isCollapsed) && (
                  <ul className="admin-sidebar-list">
                    {area.items.map((item) => {
                      const section = item.section || item.id;
                      const isActive = item.id === activeNavId;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            className={`admin-sidebar-item${isActive ? ' is-active' : ''}`}
                            onClick={() => onNavigate(section, item.queue ?? '')}
                          >
                            <i className={`fas ${item.icon}`} aria-hidden />
                            {!sidebarCollapsed ? <span>{item.label}</span> : null}
                            <NavBadge count={item.badgeKey ? badges[item.badgeKey] : 0} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          {currentUser && !sidebarCollapsed ? (
            <p className="admin-sidebar-user">
              <i className="fas fa-user-circle" aria-hidden />
              {currentUser.name}
            </p>
          ) : null}
          <button type="button" className="admin-sidebar-foot-btn" onClick={onGoHome} title="Ir al sitio">
            <i className="fas fa-home" aria-hidden />
            {!sidebarCollapsed ? 'Ir al sitio' : null}
          </button>
          <button
            type="button"
            className="admin-sidebar-foot-btn admin-sidebar-foot-btn--logout"
            onClick={onLogout}
            title="Cerrar sesión"
          >
            <i className="fas fa-sign-out-alt" aria-hidden />
            {!sidebarCollapsed ? 'Cerrar sesión' : null}
          </button>
        </div>
      </aside>

      <div className="admin-main">
        {processLink ? (
          <div className="admin-process-bar">
            <span>
              Proceso: <strong>{processLink.code}</strong> — {processLink.label}
            </span>
            {processLink.notionUrl ? (
              <a href={processLink.notionUrl} target="_blank" rel="noopener noreferrer">
                Abrir en Notion
              </a>
            ) : (
              <span className="admin-process-doc" title={processLink.repoDoc}>
                Doc: {processLink.repoDoc}
              </span>
            )}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
